import React, { useState, useEffect, useMemo } from 'react';
import './ItemModal.css';
import { useCart } from '../context/CartContext';
import ErrorModal from './ErrorModal';

const ItemModal = ({ isOpen, item, onClose, onSuccess }) => {
    const [selectedRequiredOption, setSelectedRequiredOption] = useState(null);
    const [selectedOptionalOptions, setSelectedOptionalOptions] = useState([]);
    const [selectedMainProteins, setSelectedMainProteins] = useState([]);
    const [selectedExtraProteins, setSelectedExtraProteins] = useState([]);
    const [quantity, setQuantity] = useState(1);
    const [errorMessage, setErrorMessage] = useState("");
    const [showMainProteins, setShowMainProteins] = useState(false);
    const [showExtraProteins, setShowExtraProteins] = useState(false);
    const [showSwallowOptions, setShowSwallowOptions] = useState(false);

    const { addToCart } = useCart();

    const turkeyId = 5;
    const swallowOptionIds = useMemo(() => [6, 7, 8, 9, 92, 93, 98], []);
    const noSwallowId = 98;

    const isExtraOnlyItem = item?.id === "84";


    useEffect(() => {
        if (isOpen && item) {
            const swallowOptions = item.optional_options.filter(opt =>
                swallowOptionIds.includes(Number(opt.id))
            );
            if (swallowOptions.length > 0) {
                setShowSwallowOptions(true);
                setShowMainProteins(false);
            } else {
                setShowSwallowOptions(false);
                setShowMainProteins(true);
            }
            setShowExtraProteins(false);
        }
    }, [isOpen, item, swallowOptionIds]);


    if (!isOpen || !item) return null;

    const handleRequiredChange = (option) => {
        setSelectedRequiredOption(option);
    };

    const handleOptionalChange = (option) => {
        const optionId = parseInt(option.id, 10);
        const existing = selectedOptionalOptions.find(opt => parseInt(opt.id, 10) === optionId);
        if (existing) {
            setSelectedOptionalOptions(selectedOptionalOptions.filter(opt => parseInt(opt.id, 10) !== optionId));
        } else {
            setSelectedOptionalOptions([...selectedOptionalOptions, { ...option, quantity: 1 }]);
        }
    };

    const updateOptionalQuantity = (optionId, change) => {
        setSelectedOptionalOptions(prevOptions =>
            prevOptions.map(opt =>
                parseInt(opt.id, 10) === parseInt(optionId, 10)
                    ? { ...opt, quantity: Math.max(1, opt.quantity + change) }
                    : opt
            )
        );
    };

    const getMainProteinTotal = () => selectedMainProteins.reduce((sum, item) => sum + item.quantity, 0);

    const updateMainProteinQuantity = (optionId, change) => {
        setSelectedMainProteins(prevOptions => {
            const total = getMainProteinTotal();
            return prevOptions.map(opt => {
                if (parseInt(opt.id, 10) === parseInt(optionId, 10)) {
                    const newQty = opt.quantity + change;
                    const newTotal = total + change;
                    if (newQty < 1 || newTotal > 2) return opt;
                    return { ...opt, quantity: newQty };
                }
                return opt;
            });
        });
    };

    const updateExtraProteinQuantity = (optionId, change) => {
        setSelectedExtraProteins(prevOptions =>
            prevOptions.map(opt =>
                parseInt(opt.id, 10) === parseInt(optionId, 10)
                    ? { ...opt, quantity: Math.max(1, opt.quantity + change) }
                    : opt
            )
        );
    };

    const handleMainProteinChange = (option) => {
        const isTurkey = Number(option.id) === turkeyId;
        const isAlreadySelected = selectedMainProteins.some(opt => opt.id === option.id);

        if (isAlreadySelected) {
            // If already selected, remove it
            setSelectedMainProteins(prev =>
                prev.filter(opt => opt.id !== option.id)
            );
        } else {
            if (isTurkey) {
                // Turkey selected: remove all other selections and add Turkey
                setSelectedMainProteins([{ ...option, quantity: 1 }]);
            } else {
                const turkeySelected = selectedMainProteins.some(opt => Number(opt.id) === turkeyId);
                if (turkeySelected) {
                    // Another protein selected while Turkey is selected: replace Turkey with new selection
                    setSelectedMainProteins([{ ...option, quantity: 1 }]);
                } else {
                    // Normal selection (if under 2)
                    if (selectedMainProteins.length < 2) {
                        setSelectedMainProteins(prev => [
                            ...prev,
                            { ...option, quantity: 1 }
                        ]);
                    }
                }
            }
        }
    };


    const handleExtraProteinChange = (option) => {
        const exists = selectedExtraProteins.find(opt => opt.id === option.id);
        if (exists) {
            setSelectedExtraProteins(selectedExtraProteins.filter(opt => opt.id !== option.id));
        } else {
            setSelectedExtraProteins([...selectedExtraProteins, { ...option, quantity: 1 }]);
        }
    };

    const increaseQuantity = () => setQuantity(quantity + 1);
    const decreaseQuantity = () => { if (quantity > 1) setQuantity(quantity - 1); };

    const calculateTotalPrice = () => {
        let totalPrice = parseFloat(item.price);

        if (selectedRequiredOption) {
            totalPrice += parseFloat(selectedRequiredOption.option_price);
        }

        selectedOptionalOptions.forEach(option => {
            totalPrice += parseFloat(option.option_price) * option.quantity;
        });

        selectedMainProteins.forEach(option => {
            const isTurkey = Number(option.id) === turkeyId;
            const price = isTurkey ? 1.00 : 0.00;
            totalPrice += price * option.quantity;
        });


        selectedExtraProteins.forEach(option => {
            totalPrice += parseFloat(option.option_price) * option.quantity;
        });

        return totalPrice * quantity;
    };

    const handleAddToCart = () => {
        if ((item.required_options?.length || 0) > 0 && !selectedRequiredOption) {
            setErrorMessage('Please select a required option.');
            return;
        }

        if (swallowOptions.length > 0 && selectedOptionalOptions.length === 0) {
            setErrorMessage("Please select a swallow option.");
            return;
        }

        if (proteinOptions.length > 0 && !isExtraOnlyItem) {
            const mainProteinCount = getMainProteinTotal();
            const isTurkeySelected = selectedMainProteins.some(opt => Number(opt.id) === turkeyId);
            if (!isTurkeySelected && mainProteinCount !== 2) {
                setErrorMessage("Please select exactly 2 protein options.");
                return;
            }
        }


        const getMainProteinPrice = (option) => {
            return Number(option.id) === turkeyId ? 1.00 : 0.00;
        };

        const mainProteinsWithCorrectPrice = selectedMainProteins.map(opt => ({
            ...opt,
            option_price: getMainProteinPrice(opt),
        }));

        const nonSwallowOptionalOptions = item.optional_options
            .filter(opt => {
                const isSwallow = swallowOptionIds.includes(Number(opt.id));
                const isSelected = selectedOptionalOptions.some(sel => sel.id === opt.id);
                return !isSwallow && isSelected;
            })
            .map(opt => {
                const selected = selectedOptionalOptions.find(sel => sel.id === opt.id);
                return { ...selected };
            });

        const swallowSelectedOptions = selectedOptionalOptions.filter(opt =>
            swallowOptionIds.includes(Number(opt.id))
        );

        const newItem = {
            ...item,
            selectedRequiredOption,
            swallowOptions: swallowSelectedOptions,
            optionalOptions: nonSwallowOptionalOptions,
            mainProteins: mainProteinsWithCorrectPrice,
            extraProteins: selectedExtraProteins,
            quantity,
            totalPrice: calculateTotalPrice(mainProteinsWithCorrectPrice),
        };

        addToCart(newItem);
        onClose();
        onSuccess();
    };

    const getOptionDisplayPrice = (option, selected) => {
        const qty = selected ? selected.quantity : 1;
        return (parseFloat(option.option_price) * qty).toFixed(2);
    };

    const swallowOptions = item.optional_options.filter(opt => swallowOptionIds.includes(Number(opt.id)));
    const sortedSwallowOptions = [...swallowOptions].sort((a, b) => {
        if (parseInt(a.id, 10) === noSwallowId) return 1;
        if (parseInt(b.id, 10) === noSwallowId) return -1;
        return 0;
    });

    const proteinOptions = item.optional_options.filter(opt => !swallowOptionIds.includes(Number(opt.id)));
    const hasProteinOptions = proteinOptions.length > 0;

    const isNoSwallowSelected = selectedOptionalOptions.some(opt => Number(opt.id) === noSwallowId);
    const isOutOfStock = Number(item.in_stock) === 0;

    return (
        <>
            <div className="modal-overlay" onClick={onClose}>
                <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-top">
                        <h2>{item.item_name}</h2>
                        <button className="close-modal-button" onClick={onClose}><i className="fas fa-times"></i></button>
                    </div>

                    <div className='modal-body'>
                        <img
                            src={item.image ? `https://lasgidirestaurant.com/${item.image}` : '/placeholder.png'}
                            className="modal-item-image"
                            alt={item.item_name}
                        />
                        <p className="modal-item-desc">{item.item_description}</p>

                        {item.required_options?.length > 0 && (
                            <div className="modal-section">
                                <h4 className="required-header">Select an option</h4>
                                {item.required_options.map(option => (
                                    <label key={option.id} className="option-label-inline">
                                        <div className='option-info'>
                                            <input
                                                type="radio"
                                                name="requiredOption"
                                                value={option.id}
                                                checked={selectedRequiredOption?.id === option.id}
                                                onChange={() => handleRequiredChange(option)}
                                            />
                                            <span>{option.option_name} (£{parseFloat(option.option_price).toFixed(2)})</span>
                                        </div>
                                    </label>
                                ))}
                            </div>
                        )}


                        {swallowOptions.length > 0 && (
                            <div className="modal-section">
                                <div className="modal-section-heading collapsible-heading" onClick={() => {
                                    const nextState = !showSwallowOptions;
                                    setShowSwallowOptions(nextState);
                                    setShowMainProteins(false);
                                    setShowExtraProteins(false);
                                }}>

                                    <h4>Swallow Options</h4>
                                    <span className={`toggle-icon ${showSwallowOptions ? 'rotate' : ''}`}>&#9654;</span>
                                </div>
                                {showSwallowOptions && (
                                    sortedSwallowOptions.map(option => {
                                        const selected = selectedOptionalOptions.find(opt => Number(opt.id) === Number(option.id));
                                        const qty = selected ? selected.quantity : 1;
                                        const isDisabled = isNoSwallowSelected && Number(option.id) !== noSwallowId;

                                        return (
                                            <label key={option.id} className="option-label-inline">
                                                <div className="option-info">
                                                    <input
                                                        type="checkbox"
                                                        checked={!!selected}
                                                        onChange={() => handleOptionalChange(option)}
                                                        disabled={isDisabled}
                                                    />
                                                    <span>{option.option_name} (£{getOptionDisplayPrice(option, selected)})</span>
                                                </div>
                                                {option.allow_quantity && selected && (
                                                    <div className="quantity-control-inline">
                                                        <button onClick={() => updateOptionalQuantity(option.id, -1)} disabled={qty <= 1}>-</button>
                                                        <span className="quantity-value-inline">{qty}</span>
                                                        <button onClick={() => updateOptionalQuantity(option.id, 1)}>+</button>
                                                    </div>
                                                )}
                                            </label>
                                        );
                                    })
                                )}
                            </div>
                        )}

                        {hasProteinOptions && (
                            <>
                                {!isExtraOnlyItem && (
                                    <div className="modal-section">
                                        <div className="modal-section-heading collapsible-heading" onClick={() => {
                                            const nextState = !showMainProteins;
                                            setShowMainProteins(nextState);
                                            setShowSwallowOptions(false);
                                            setShowExtraProteins(false);
                                        }}>
                                            <h4>Protein Options (Pick 2)</h4>
                                            <span className={`toggle-icon ${showMainProteins ? 'rotate' : ''}`}>&#9654;</span>
                                        </div>
                                        {showMainProteins && (
                                            <div className="options-list">
                                                {proteinOptions
                                                    .filter(option => ![83, 101].includes(Number(option.id))) // exclude plantain & beans
                                                    .map(option => {
                                                        const selected = selectedMainProteins.find(opt => opt.id === option.id);
                                                        const isTurkey = Number(option.id) === turkeyId;
                                                        const isTurkeySelected = selectedMainProteins.some(opt => Number(opt.id) === turkeyId);
                                                        const optionQuantity = selected ? selected.quantity : 1;
                                                        const totalQty = getMainProteinTotal();

                                                        const isDisabled = (() => {
                                                            if (isTurkeySelected) {
                                                                return !isTurkey; // disable all other options
                                                            } else {
                                                                return !selected && totalQty >= 2; // disable if 2 selected already
                                                            }
                                                        })();

                                                        return (
                                                            <label key={option.id} className="option-label-inline">
                                                                <div className="option-info">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={!!selected}
                                                                        onChange={() => handleMainProteinChange(option)}
                                                                        disabled={isDisabled}
                                                                    />
                                                                    <span>{option.option_name} (£{isTurkey ? "1.00" : "0.00"})</span>
                                                                </div>
                                                                {selected && (
                                                                    <div className="quantity-control-inline">
                                                                        <button onClick={() => updateMainProteinQuantity(option.id, -1)} disabled={optionQuantity <= 1}>-</button>
                                                                        <span className="quantity-value-inline">{optionQuantity}</span>
                                                                        <button onClick={() => updateMainProteinQuantity(option.id, 1)} disabled={totalQty >= 2 || isTurkeySelected}>+</button>
                                                                    </div>
                                                                )}
                                                            </label>
                                                        );
                                                    })}
                                            </div>
                                        )}
                                    </div>
                                )}

                                <div className="modal-section">
                                    <div className="modal-section-heading collapsible-heading" onClick={() => {
                                        const nextState = !showExtraProteins;
                                        setShowExtraProteins(nextState);
                                        setShowSwallowOptions(false);
                                        setShowMainProteins(false);
                                    }}>
                                        <h4>Extra</h4>
                                        <span className={`toggle-icon ${showExtraProteins ? 'rotate' : ''}`}>&#9654;</span>
                                    </div>
                                    {showExtraProteins && (
                                        <div className="options-list">
                                            {proteinOptions.map(option => {
                                                const selected = selectedExtraProteins.find(opt => opt.id === option.id);
                                                const qty = selected ? selected.quantity : 1;

                                                return (
                                                    <label key={option.id} className="option-label-inline">
                                                        <div className="option-info">
                                                            <input
                                                                type="checkbox"
                                                                checked={!!selected}
                                                                onChange={() => handleExtraProteinChange(option)}
                                                            />
                                                            <span>{option.option_name} (£{(parseFloat(option.option_price) * qty).toFixed(2)})</span>
                                                        </div>
                                                        {selected && (
                                                            <div className="quantity-control-inline">
                                                                <button onClick={() => updateExtraProteinQuantity(option.id, -1)} disabled={qty <= 1}>-</button>
                                                                <span className="quantity-value-inline">{qty}</span>
                                                                <button onClick={() => updateExtraProteinQuantity(option.id, 1)}>+</button>
                                                            </div>
                                                        )}
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </>
                        )}

                    </div>

                    <div className='modal-bottom'>
                        <div className="quantity-section">
                            <button onClick={decreaseQuantity} className="quantity-btn" disabled={isOutOfStock}>-</button>
                            <span className={`quantity-value ${isOutOfStock ? "disabled" : ""}`}>{quantity}</span>
                            <button onClick={increaseQuantity} className="quantity-btn" disabled={isOutOfStock}>+</button>
                        </div>

                        <button className="add-to-cart-button" onClick={handleAddToCart} disabled={isOutOfStock}>
                            Add to Cart for £{calculateTotalPrice().toFixed(2)}
                        </button>
                    </div>
                </div>
            </div>

            {errorMessage && (
                <ErrorModal
                    message={errorMessage}
                    onClose={() => setErrorMessage("")}
                />
            )}
        </>
    );
};

export default ItemModal;